import { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { usePublishPost, useScheduleLimit } from '@/api/hooks/usePoster'
import { Send, Clock, Layers, Calendar, Instagram, Facebook, Youtube, Loader2, Shuffle, MessageCircle } from 'lucide-react'
import { toast } from 'sonner'
import { getScheduleTimes, getBatchScheduleTime, buildDayRotatedClipSchedule, extractClipSource, toLocalISO } from './utils'
import type { Account } from '@/types'
import type { WizardState, WizardAction } from './types'

const CATEGORY_SUGGESTIONS = ['Promocao', 'Conteudo', 'Engajamento', 'Lancamento', 'Informativo']

const MODES = [
  { key: 'now' as const, label: 'Agora', icon: Send, desc: 'Publicar imediatamente' },
  { key: 'single' as const, label: 'Horario fixo', icon: Clock, desc: 'Mesmo horario + intervalo' },
  { key: 'spread' as const, label: 'Distribuido', icon: Layers, desc: 'Espalhar no dia' },
  { key: 'daily' as const, label: '1 por dia', icon: Calendar, desc: 'Um video por dia' },
]

interface Props {
  state: WizardState
  dispatch: React.Dispatch<WizardAction>
  accounts: Account[]
  onClose: () => void
}

export function WizardStep3({ state, dispatch, accounts, onClose }: Props) {
  const [submitting, setSubmitting] = useState(false)
  const [progress, setProgress] = useState({ done: 0, total: 0 })
  const publishPost = usePublishPost()
  const { data: scheduleLimit } = useScheduleLimit()

  const hasIG = useMemo(() => Object.values(state.selectedAccounts).some(v => v.ig), [state.selectedAccounts])
  const hasFB = useMemo(() => Object.values(state.selectedAccounts).some(v => v.fb), [state.selectedAccounts])
  const hasYT = useMemo(() => Object.values(state.selectedYtChannels).some(v => v), [state.selectedYtChannels])

  const destCount = useMemo(() => {
    let c = 0
    for (const v of Object.values(state.selectedAccounts)) { if (v.ig) c++; if (v.fb) c++ }
    for (const v of Object.values(state.selectedYtChannels)) { if (v) c++ }
    return c
  }, [state.selectedAccounts, state.selectedYtChannels])

  // In clip mode: each source visits each dest once = items * destCount still correct
  // (every clip goes to every page, just on different days)
  const totalPosts = state.items.length * destCount

  // Schedule times
  const times = useMemo(() => getScheduleTimes(state.scheduleMode, state.items.length, {
    scheduleAt: state.scheduleAt,
    spreadStartTime: state.spreadStartTime,
    spreadEndTime: state.spreadEndTime,
    dailyStartDate: state.dailyStartDate,
    dailyTime: state.dailyTime,
  }), [state.scheduleMode, state.items.length, state.scheduleAt, state.spreadStartTime, state.spreadEndTime, state.dailyStartDate, state.dailyTime])

  const daysSpan = useMemo(() => {
    if (state.scheduleMode !== 'daily' || !times[0] || !times[times.length - 1]) return 0
    const first = new Date(times[0])
    const last = new Date(times[times.length - 1])
    return Math.round((last.getTime() - first.getTime()) / 86400000) + 1
  }, [times, state.scheduleMode])

  // Detect clip sources for shuffle mode
  const clipSources = useMemo(() => {
    const sources = new Set<string>()
    state.items.forEach(i => sources.add(extractClipSource(i.filename)))
    return sources
  }, [state.items])
  const hasMultipleSources = clipSources.size > 1
  const isClipMode = state.shuffleClips && destCount > 1

  // Build payloads
  const buildAccountsPayload = () => {
    const result: Array<{ igUserId: string; platforms: string[] }> = []
    for (const [igUserId, p] of Object.entries(state.selectedAccounts)) {
      const plats: string[] = []
      if (p.ig) plats.push('ig')
      if (p.fb) plats.push('fb')
      if (plats.length > 0) result.push({ igUserId, platforms: plats })
    }
    return result
  }

  const buildYtPayload = () =>
    Object.entries(state.selectedYtChannels).filter(([, v]) => v).map(([id]) => id)

  const handleSubmit = async () => {
    const isScheduling = state.scheduleMode !== 'now'
    if (isScheduling) {
      if (state.scheduleMode === 'single' && !state.scheduleAt) { toast.error('Selecione data/hora'); return }
      if (state.scheduleMode === 'spread' && !state.scheduleAt) { toast.error('Selecione a data'); return }
      if (state.scheduleMode === 'daily' && !state.dailyStartDate) { toast.error('Selecione a data de inicio'); return }
    }

    const accountsPayload = buildAccountsPayload()
    const ytPayload = buildYtPayload()

    if (isScheduling && scheduleLimit && !scheduleLimit.unlimited) {
      if (scheduleLimit.remaining !== null && totalPosts > scheduleLimit.remaining) {
        toast.error(`Limite: ${scheduleLimit.current}/${scheduleLimit.limit}. Restam ${scheduleLimit.remaining} vagas.`)
        return
      }
    }

    const label = isScheduling ? `Agendar ${totalPosts} post(s)?` : `Publicar ${totalPosts} post(s)?`
    if (!confirm(label)) return

    setSubmitting(true)
    let published = 0, scheduled = 0, errors = 0
    const action: 'now' | 'schedule' = isScheduling ? 'schedule' : 'now'
    const category = state.category === 'custom' ? state.customCategory.trim() : state.category

    const publishItem = async (item: typeof state.items[0], scheduleAt: string | undefined, accs: typeof accountsPayload, yt: string[]) => {
      const caption = state.overrides[item.id]?.caption ?? state.globalCaption
      const ytTitle = state.overrides[item.id]?.ytTitle ?? state.globalYtTitle
      const ytDesc = state.overrides[item.id]?.ytDescription ?? state.globalYtDescription
      const data = await publishPost.mutateAsync({
        accounts: accs,
        title: state.postTitle.trim() || undefined,
        message: caption.trim(),
        imageUrl: item.url,
        scheduleAt,
        action,
        category: category || undefined,
        mediaType: item.mediaType,
        postType: state.postType,
        youtubeChannels: yt.length > 0 ? yt : undefined,
        youtubeTitle: hasYT ? (ytTitle.trim() || undefined) : undefined,
        youtubeDescription: hasYT ? (ytDesc.trim() || undefined) : undefined,
        youtubeTags: hasYT && state.ytTags.trim() ? state.ytTags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
        youtubePrivacy: hasYT ? state.ytPrivacy : undefined,
        preferredProfile: state.preferredProfile || undefined,
        antiFingerprint: state.antiFingerprint && item.mediaType === 'image' ? true : undefined,
        ctaSendMessage: state.ctaSendMessage || undefined,
      })
      published += data.results.filter((r: { published?: boolean }) => r.published).length
      scheduled += data.results.filter((r: { scheduled?: boolean }) => r.scheduled).length
      errors += data.results.filter((r: { error?: string }) => r.error).length
    }

    if (isClipMode) {
      // DISTRIBUTE CLIPS MODE: each source video goes to 1 page per day,
      // sequential with 1-min interval. Same source never on 2 pages same day.
      // Next day the sources rotate to different pages.

      // Build flat list of individual destinations
      const individualDests: Array<{ accounts: typeof accountsPayload; yt: string[] }> = []
      for (const acc of accountsPayload) {
        // Group IG+FB for same account as one destination (same page)
        individualDests.push({
          accounts: [{ igUserId: acc.igUserId, platforms: acc.platforms }],
          yt: [],
        })
      }
      for (const ytId of ytPayload) {
        individualDests.push({ accounts: [], yt: [ytId] })
      }

      const ops = buildDayRotatedClipSchedule(state.items, individualDests.length)
      setProgress({ done: 0, total: ops.length })

      // Calculate base date/time from schedule config
      const baseDate = state.scheduleMode === 'daily'
        ? state.dailyStartDate
        : state.scheduleMode === 'spread'
          ? (state.scheduleAt ? state.scheduleAt.split('T')[0] : '')
          : (state.scheduleAt ? state.scheduleAt.split('T')[0] : '')
      const baseTime = state.scheduleMode === 'daily'
        ? state.dailyTime
        : state.scheduleMode === 'spread'
          ? state.spreadStartTime
          : (state.scheduleAt ? state.scheduleAt.split('T')[1] || '10:00' : '10:00')

      let done = 0
      for (const op of ops) {
        const dest = individualDests[op.destIndex]
        // Schedule: base date + dayOffset days, base time + clipIndex minutes
        let itemSchedule: string | undefined
        if (isScheduling && baseDate) {
          const d = new Date(`${baseDate}T${baseTime}`)
          d.setDate(d.getDate() + op.dayOffset)
          d.setMinutes(d.getMinutes() + op.clipIndex) // 1 min interval between clips
          itemSchedule = toLocalISO(d)
        }
        try {
          await publishItem(op.item, itemSchedule, dest.accounts, dest.yt)
        } catch {
          errors++
        }
        done++
        setProgress({ done, total: ops.length })
      }
    } else {
      // NORMAL MODE: all items to all destinations
      setProgress({ done: 0, total: state.items.length })
      for (let i = 0; i < state.items.length; i++) {
        const item = state.items[i]
        const itemSchedule = isScheduling
          ? getBatchScheduleTime(i, state.scheduleMode, state.items.length, state.batchInterval, state.scheduleAt, times)
          : undefined
        try {
          await publishItem(item, itemSchedule, accountsPayload, ytPayload)
        } catch {
          errors += destCount
        }
        setProgress({ done: i + 1, total: state.items.length })
      }
    }

    setSubmitting(false)
    if (published) toast.success(`${published} publicado(s)`)
    if (scheduled) toast.success(`${scheduled} agendado(s)`)
    if (errors) toast.error(`${errors} erro(s)`)
    dispatch({ type: 'RESET' })
    onClose()
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-5">
        {/* Schedule modes */}
        <div className="grid grid-cols-4 gap-2 mb-5">
          {MODES.map(m => (
            <button key={m.key} type="button"
              onClick={() => dispatch({ type: 'SET_FIELD', field: 'scheduleMode', value: m.key })}
              className={cn('rounded-xl border p-3.5 text-center transition-all',
                state.scheduleMode === m.key
                  ? 'border-[#c9a84c] bg-[#c9a84c]/8 shadow-[0_0_16px_rgba(201,168,76,0.08)]'
                  : 'border-[rgba(255,255,255,0.09)] hover:border-[rgba(255,255,255,0.15)] hover:bg-[#222226]')}>
              <m.icon className={cn('mx-auto h-5 w-5 mb-1.5', state.scheduleMode === m.key ? 'text-[#c9a84c]' : 'text-[#888580]')} />
              <p className={cn('text-xs font-semibold', state.scheduleMode === m.key ? 'text-[#c9a84c]' : 'text-[#f5f5f3]')}>{m.label}</p>
              <p className="text-[9px] text-[#888580] mt-0.5">{m.desc}</p>
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-5">
          {/* Config */}
          <div className="rounded-xl border border-[rgba(255,255,255,0.09)] bg-[#151518] p-4">
            <h4 className="text-xs font-semibold text-[#c9a84c] mb-3">Configuracao</h4>

            {state.scheduleMode === 'single' && (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <label className="text-[11px] text-[#888580] w-20 shrink-0">Data/hora</label>
                  <Input type="datetime-local" value={state.scheduleAt}
                    onChange={e => dispatch({ type: 'SET_FIELD', field: 'scheduleAt', value: e.target.value })}
                    className="h-8 text-xs bg-transparent border-[rgba(255,255,255,0.09)]" />
                </div>
                {state.items.length > 1 && (
                  <div className="flex items-center gap-3">
                    <label className="text-[11px] text-[#888580] w-20 shrink-0">Intervalo</label>
                    <Input type="number" value={state.batchInterval} min={1}
                      onChange={e => dispatch({ type: 'SET_FIELD', field: 'batchInterval', value: Number(e.target.value) })}
                      className="h-8 w-20 text-xs bg-transparent border-[rgba(255,255,255,0.09)]" />
                    <span className="text-[10px] text-[#888580]">min</span>
                  </div>
                )}
              </div>
            )}

            {state.scheduleMode === 'spread' && (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <label className="text-[11px] text-[#888580] w-20 shrink-0">Data</label>
                  <Input type="date" value={state.scheduleAt ? state.scheduleAt.split('T')[0] : ''}
                    onChange={e => dispatch({ type: 'SET_FIELD', field: 'scheduleAt', value: `${e.target.value}T${state.spreadStartTime}` })}
                    className="h-8 text-xs bg-transparent border-[rgba(255,255,255,0.09)]" />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-[11px] text-[#888580] w-20 shrink-0">Inicio</label>
                  <Input type="time" value={state.spreadStartTime}
                    onChange={e => dispatch({ type: 'SET_FIELD', field: 'spreadStartTime', value: e.target.value })}
                    className="h-8 w-28 text-xs bg-transparent border-[rgba(255,255,255,0.09)]" />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-[11px] text-[#888580] w-20 shrink-0">Fim</label>
                  <Input type="time" value={state.spreadEndTime}
                    onChange={e => dispatch({ type: 'SET_FIELD', field: 'spreadEndTime', value: e.target.value })}
                    className="h-8 w-28 text-xs bg-transparent border-[rgba(255,255,255,0.09)]" />
                </div>
              </div>
            )}

            {state.scheduleMode === 'daily' && (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <label className="text-[11px] text-[#888580] w-20 shrink-0">Data inicio</label>
                  <Input type="date" value={state.dailyStartDate}
                    onChange={e => dispatch({ type: 'SET_FIELD', field: 'dailyStartDate', value: e.target.value })}
                    className="h-8 text-xs bg-transparent border-[rgba(255,255,255,0.09)]" />
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-[11px] text-[#888580] w-20 shrink-0">Horario</label>
                  <Input type="time" value={state.dailyTime}
                    onChange={e => dispatch({ type: 'SET_FIELD', field: 'dailyTime', value: e.target.value })}
                    className="h-8 w-28 text-xs bg-transparent border-[rgba(255,255,255,0.09)]" />
                </div>
              </div>
            )}

            {state.scheduleMode === 'now' && (
              <p className="text-[11px] text-[#888580]">Os videos serao publicados imediatamente apos confirmar.</p>
            )}

            {/* Category */}
            <div className="mt-4 pt-3 border-t border-[rgba(255,255,255,0.09)]">
              <label className="text-[10px] font-semibold text-[#888580] uppercase tracking-wider mb-2 block">Categoria</label>
              <div className="flex flex-wrap gap-1">
                {CATEGORY_SUGGESTIONS.map(cat => (
                  <button key={cat} type="button"
                    onClick={() => dispatch({ type: 'SET_FIELD', field: 'category', value: state.category === cat ? '' : cat })}
                    className={cn('rounded-lg border px-2.5 py-1 text-[10px] font-medium transition-all',
                      state.category === cat
                        ? 'border-[#c9a84c]/30 bg-[#c9a84c]/10 text-[#c9a84c]'
                        : 'border-[rgba(255,255,255,0.09)] text-[#888580] hover:text-[#d0cdc7]')}>
                    {cat}
                  </button>
                ))}
                <button type="button"
                  onClick={() => dispatch({ type: 'SET_FIELD', field: 'category', value: state.category === 'custom' ? '' : 'custom' })}
                  className={cn('rounded-lg border px-2.5 py-1 text-[10px] font-medium transition-all',
                    state.category === 'custom'
                      ? 'border-[#c9a84c]/30 bg-[#c9a84c]/10 text-[#c9a84c]'
                      : 'border-[rgba(255,255,255,0.09)] text-[#888580] hover:text-[#d0cdc7]')}>
                  Outro...
                </button>
              </div>
              {state.category === 'custom' && (
                <Input value={state.customCategory}
                  onChange={e => dispatch({ type: 'SET_FIELD', field: 'customCategory', value: e.target.value })}
                  placeholder="Nome da categoria..." className="h-7 mt-1.5 text-xs bg-transparent border-[rgba(255,255,255,0.09)]" />
              )}
            </div>

            {/* CTA Send Message — only for FB feed posts */}
            {hasFB && state.postType === 'feed' && (
              <div className="mt-3 flex items-center gap-2">
                <input type="checkbox" checked={state.ctaSendMessage}
                  onChange={e => dispatch({ type: 'SET_FIELD', field: 'ctaSendMessage', value: e.target.checked })}
                  className="rounded" />
                <MessageCircle className={cn('h-3.5 w-3.5', state.ctaSendMessage ? 'text-[#4488ff]' : 'text-[#888580]')} />
                <span className={cn('text-[10px]', state.ctaSendMessage ? 'text-[#4488ff] font-semibold' : 'text-[#888580]')}>
                  Botao "Enviar Mensagem" (FB)
                </span>
              </div>
            )}

            {/* Shuffle clips */}
            {destCount > 1 && state.items.length > 1 && (
              <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.09)]">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={state.shuffleClips}
                    onChange={e => dispatch({ type: 'SET_FIELD', field: 'shuffleClips', value: e.target.checked })}
                    className="rounded" />
                  <Shuffle className={cn('h-3.5 w-3.5', state.shuffleClips ? 'text-[#c9a84c]' : 'text-[#888580]')} />
                  <span className={cn('text-[10px] font-semibold', state.shuffleClips ? 'text-[#c9a84c]' : 'text-[#888580]')}>
                    Distribuir cortes por pagina/dia
                  </span>
                </label>
                <p className="text-[9px] text-[#888580] mt-1 ml-6">
                  Cada pagina recebe 1 video por dia (partes sequenciais, 1 min intervalo).
                  Mesmo video nunca em 2 paginas no mesmo dia.
                  {clipSources.size > 1 && ` ${clipSources.size} fontes detectadas.`}
                </p>
              </div>
            )}
          </div>

          {/* Timeline */}
          <div className="rounded-xl border border-[rgba(255,255,255,0.09)] bg-[#151518] p-4">
            <h4 className="text-[10px] font-semibold text-[#888580] uppercase tracking-wider mb-3">Preview da Timeline</h4>
            {state.items.map((item, idx) => {
              const time = state.scheduleMode === 'now' ? 'Agora' :
                (() => {
                  const t = getBatchScheduleTime(idx, state.scheduleMode, state.items.length, state.batchInterval, state.scheduleAt, times)
                  if (!t) return '--'
                  const d = new Date(t)
                  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
                })()
              return (
                <div key={item.id} className="flex items-center gap-3 py-2 border-b border-[rgba(255,255,255,0.09)] last:border-0">
                  <span className="text-xs font-semibold text-[#c9a84c] w-[70px] shrink-0">{time}</span>
                  <span className="text-xs text-[#d0cdc7] flex-1 truncate">{item.filename || `Video ${idx + 1}`}</span>
                  <div className="flex gap-1">
                    {hasIG && <span className="rounded bg-[#c73eb8]/12 px-1.5 py-0.5 text-[8px] font-bold text-[#c73eb8]">IG</span>}
                    {hasFB && <span className="rounded bg-[#4488ff]/12 px-1.5 py-0.5 text-[8px] font-bold text-[#4488ff]">FB</span>}
                    {hasYT && <span className="rounded bg-[#ff4444]/12 px-1.5 py-0.5 text-[8px] font-bold text-[#ff4444]">YT</span>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Summary bar */}
      <div className="shrink-0 flex items-center gap-5 border-t border-[rgba(255,255,255,0.09)] bg-[#c9a84c]/3 px-5 py-3">
        <SumStat value={state.items.length} label="Videos" />
        <div className="w-px h-8 bg-[rgba(255,255,255,0.09)]" />
        <SumStat value={destCount} label="Destinos" />
        <div className="w-px h-8 bg-[rgba(255,255,255,0.09)]" />
        <SumStat value={totalPosts} label="Posts" />
        {daysSpan > 0 && (
          <>
            <div className="w-px h-8 bg-[rgba(255,255,255,0.09)]" />
            <SumStat value={daysSpan} label="Dias" />
          </>
        )}
        <div className="flex-1" />
        {scheduleLimit && !scheduleLimit.unlimited && (
          <span className={cn('text-[11px]', scheduleLimit.remaining !== null && totalPosts > scheduleLimit.remaining ? 'text-[#dc5046]' : 'text-[#22c55e]')}>
            {scheduleLimit.current}/{scheduleLimit.limit} slots
            {scheduleLimit.remaining !== null && ` · ${scheduleLimit.remaining} restantes`}
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="shrink-0 flex items-center justify-between border-t border-[rgba(255,255,255,0.09)] bg-black/15 px-5 py-3">
        <Button variant="outline" size="sm"
          onClick={() => dispatch({ type: 'SET_STEP', step: 2 })}
          disabled={submitting}
          className="border-[rgba(255,255,255,0.09)] text-[#888580] hover:text-[#f5f5f3]">
          Voltar
        </Button>
        <Button size="sm" onClick={handleSubmit} disabled={submitting}
          className="gap-2 px-6 py-2 bg-gradient-to-r from-[#c9a84c] to-[#9a7b2f] text-[#151518] font-bold text-sm hover:shadow-[0_4px_20px_rgba(201,168,76,0.3)]">
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {progress.done}/{progress.total}
            </>
          ) : (
            <>
              <Send className="h-4 w-4" />
              {state.scheduleMode !== 'now' ? `Agendar ${totalPosts} Posts` : `Publicar ${totalPosts} Posts`}
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

function SumStat({ value, label }: { value: number; label: string }) {
  return (
    <div className="text-center">
      <div className="text-xl font-extrabold text-[#c9a84c]">{value}</div>
      <div className="text-[9px] text-[#888580]">{label}</div>
    </div>
  )
}
